import { PAGE_CACHE_HEADERS } from "@/lib/ssrCache";
import { ensureExists } from "@/lib/ensureExists";
import { createFileRoute, redirect, notFound } from "@tanstack/react-router";
import { lawnMowerProductQueryOptions } from "@/lib/productPageQuery";
import { currentYear } from "@/lib/siteYear";
import LawnMowerProduct from "@/pages/LawnMowerProduct";
import { redirectIfPruned } from "@/lib/prunedRedirect";

// Ported from pre-migration App.tsx <Navigate> routes: grass trimmers moved to /strimmers
const STRIMMER_REDIRECTS: Record<string, string> = {
  "hyundai-HYWT5200X-52cc-petrol-wheeled-grass-trimmer-43cm-cutting-width-large-wheel":
    "/strimmers/hyundai-HYWT5200X-52cc-petrol-wheeled-grass-trimmer-43cm-cutting-width-large-wheel",
  "hyundai-HYBC5600X-56cc-petrol-brushcutter-and-grass-trimmer-2-in-1-includes-harnes":
    "/strimmers/hyundai-HYBC5600X-56cc-petrol-brushcutter-and-grass-trimmer-2-in-1-includes-harnes",
  "hyundai-HYTR60LI-60v-cordless-battery-grass-trimmer-bare-unit-33cm-cutting-width":
    "/strimmers/hyundai-HYTR60LI-60v-cordless-battery-grass-trimmer-bare-unit-33cm-cutting-width",
};

export const Route = createFileRoute("/lawn-mowers/$slug")({
  beforeLoad: async ({ params }) => {
    const target = STRIMMER_REDIRECTS[params.slug];
    if (target) {
      throw redirect({ href: target, replace: true });
    }
    await redirectIfPruned("lawn-mowers", params.slug);
    await ensureExists("lawn_mowers", params.slug);
  },
  loader: async ({ params, context }) => {
    const product = await context.queryClient.ensureQueryData(lawnMowerProductQueryOptions(params.slug));
    if (!product) throw notFound();
    return { product };
  },
  component: LawnMowerProduct,
  headers: () => PAGE_CACHE_HEADERS,
  head: ({ params, loaderData }) => {
    const canonicalUrl = "https://www.garden-review.co.uk" + `/lawn-mowers/${params.slug}`;
    const mower = loaderData?.product;
    if (!mower) return {};
    const model = ((mower.model || "").split(/[\s—–\-(]/).find(Boolean) || mower.model || "").trim();
    const year = currentYear();
    const title = `${mower.brand} ${model} Review (${year}) — Real-World UK Test`;
    const description = `${mower.brand} ${model} review (${year}, UK) — real-world test, verdict, pros & cons, full specs and the cheapest UK price.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(mower.image_url?.startsWith("https://") ? [
          { property: "og:image", content: mower.image_url },
          { name: "twitter:image", content: mower.image_url },
        ] : []),
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
});
